/**
 * EventsMonitor — FEAT-MONITOR-001 (Events Monitor tab)
 * Monitors the rate at which xNotify RECEIVES events (SDK/tracking ingest, from ClickHouse),
 * overall and per account. Distinct from the message monitor: events have no provisioned TPS
 * ceiling, so this is throughput + volume + breakdown (top events, top emitting accounts).
 */
import React, { useMemo, useState } from "react";
import {
  Box, Flex, HStack, Text, Heading, Badge, Button, Select, Spinner,
  Table, Thead, Tbody, Tr, Th, Td, Progress, SimpleGrid, Wrap, WrapItem,
} from "@chakra-ui/react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import useEventMonitorPolling from "./useEventMonitorPolling";
import EventPipelineFlow from "./EventPipelineFlow";
import { TONE, classifyStale, fmtInt, fmtPct, fmtTps, fmtRelative, WINDOW_OPTIONS, BUCKET_HINT } from "./helpers";

const INTERVALS = [{ label: "2s", ms: 2000 }, { label: "5s", ms: 5000 }, { label: "10s", ms: 10000 }, { label: "30s", ms: 30000 }, { label: "Off", ms: 0 }];
const BUCKETS = ["1m", "5m", "15m", "1h"];

export default function EventsMonitor() {
  const [intervalMs, setIntervalMs] = useState(2000);
  const [window, setWindow] = useState("6h");
  const [bucket, setBucket] = useState(BUCKET_HINT["6h"]);
  const [customerId, setCustomerId] = useState(null);
  const [now, setNow] = useState(Date.now());

  const { overview, throughput, byCustomer, pipeline, error, stale, lastOkAt, busy, refresh } =
    useEventMonitorPolling({ intervalMs, window, bucket, customerId });

  React.useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const staleSec = lastOkAt ? Math.max(0, Math.round((now - new Date(lastOkAt).getTime()) / 1000)) : null;
  const health = staleSec == null ? "muted" : classifyStale(staleSec);

  const changeWindow = (w) => { setWindow(w); setBucket(BUCKET_HINT[w] || "5m"); };

  // Account list from the top-emitters table (no extra endpoint).
  const accounts = useMemo(() => {
    const m = new Map();
    (byCustomer?.rows || []).forEach((r) => m.set(r.customer_id, r.business_name));
    return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [byCustomer]);

  const lm = overview?.last_minute;
  const wt = overview?.window_totals;

  return (
    <Box opacity={stale ? 0.7 : 1} transition="opacity .2s">
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3} mb={5}>
        <HStack spacing={3} align="center">
          <Heading fontSize="22px" color="#111827">Events Monitor</Heading>
          <HealthPill health={health} />
          {stale && <Badge colorScheme="orange" variant="subtle">Stale</Badge>}
          <Text fontSize="xs" color="gray.500">
            {staleSec != null ? `updated ${fmtRelative(lastOkAt)}` : "polling…"}
            {busy && <Spinner size="xs" ml={2} />}
          </Text>
        </HStack>
        <Wrap spacing={2} align="center">
          <WrapItem>
            <Select size="sm" w="200px" value={customerId ?? ""} onChange={(e) => setCustomerId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">All accounts</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </Select>
          </WrapItem>
          <WrapItem>
            <Select size="sm" w="90px" value={intervalMs} onChange={(e) => setIntervalMs(Number(e.target.value))}>
              {INTERVALS.map((i) => <option key={i.label} value={i.ms}>{i.label}</option>)}
            </Select>
          </WrapItem>
          <WrapItem><Button size="sm" onClick={refresh} isLoading={busy}>Refresh</Button></WrapItem>
        </Wrap>
      </Flex>

      {error && !overview && (
        <Box p={3} mb={4} bg={TONE.crit.bg} color={TONE.crit.fg} borderRadius="md" fontSize="sm">{error}</Box>
      )}

      {/* Event-ingest lifecycle diagram */}
      {pipeline && <EventPipelineFlow pipeline={pipeline} />}

      {/* KPI row */}
      <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={3} mb={5}>
        <Kpi label="Event rate (peak)" value={fmtTps(lm?.peak_tps)} sub={`${fmtInt(lm?.count)} last min`} tone="ok" />
        <Kpi label="Event rate (avg)" value={fmtTps(lm?.avg_tps)} sub="last minute" tone="muted" />
        <Kpi label={`Events (${window})`} value={fmtInt(wt?.events)} sub="in window" tone="muted" />
        <Kpi label="Active emitters" value={fmtInt(wt?.active_emitters)} sub={customerId ? "scoped to 1" : "accounts"} tone="muted" />
        <Kpi label="Event types" value={fmtInt(wt?.event_types)} sub="distinct" tone="muted" />
      </SimpleGrid>

      {/* Throughput chart */}
      <Box className="chart_box" mb={4} p={4} bg="white" borderRadius="lg" borderWidth="1px">
        <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
          <Box>
            <Text fontWeight="700" fontSize="sm" color="#111827">Event ingest throughput</Text>
            <Text fontSize="xs" color="gray.500">events received per {bucket} bucket{customerId ? " · single account" : " · all accounts"}</Text>
          </Box>
          <HStack spacing={2}>
            <HStack spacing={1}>
              {WINDOW_OPTIONS.map((w) => (
                <Button key={w} size="xs" variant={window === w ? "solid" : "outline"} colorScheme="purple" onClick={() => changeWindow(w)}>{w}</Button>
              ))}
            </HStack>
            <Select size="xs" w="70px" value={bucket} onChange={(e) => setBucket(e.target.value)}>
              {BUCKETS.map((b) => <option key={b} value={b}>{b}</option>)}
            </Select>
          </HStack>
        </Flex>
        <EventChart series={throughput?.series || []} />
      </Box>

      {/* Top events + top emitters */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <TopEvents rows={overview?.top_events || []} />
        <TopEmitters rows={byCustomer?.rows || []} loading={!byCustomer} />
      </SimpleGrid>
    </Box>
  );
}

function HealthPill({ health }) {
  const t = TONE[health] || TONE.muted;
  const label = { ok: "Live", warn: "Lagging", crit: "Stale", muted: "—" }[health];
  return (
    <HStack spacing={1.5} px={2} py={0.5} borderRadius="full" bg={t.bg}>
      <Box w="8px" h="8px" borderRadius="full" bg={t.dot} />
      <Text fontSize="xs" fontWeight="700" color={t.fg}>{label}</Text>
    </HStack>
  );
}

function Kpi({ label, value, sub, tone }) {
  const t = TONE[tone] || TONE.muted;
  return (
    <Box p={3} bg="white" borderRadius="lg" borderWidth="1px" borderColor="gray.100">
      <Text fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.03em">{label}</Text>
      <Heading fontSize="20px" color={t.fg} mt={1}>{value}</Heading>
      {sub && <Text fontSize="11px" color="gray.400" mt={0.5}>{sub}</Text>}
    </Box>
  );
}

function EventChart({ series }) {
  const options = useMemo(() => {
    // ts is CH wall-clock "YYYY-MM-DDTHH:MM:SS" (naive) — slice HH:MM, no Date parsing / no TZ shift.
    const cats = series.map((s) => (s.ts || "").slice(11, 16));
    return {
      chart: { type: "area", height: 260 },
      title: { text: undefined },
      credits: { enabled: false },
      xAxis: { categories: cats, tickInterval: Math.ceil(cats.length / 12), labels: { style: { fontSize: "10px" } } },
      yAxis: { title: { text: "events / bucket" }, allowDecimals: false },
      legend: { enabled: false },
      plotOptions: { area: { fillOpacity: 0.18, marker: { enabled: false }, lineWidth: 2 } },
      tooltip: { shared: true },
      series: [{ name: "Events", data: series.map((s) => s.events), color: "#8B5CF6" }],
    };
  }, [series]);

  if (!series.length || series.every((s) => s.events === 0)) {
    return <Flex h="200px" align="center" justify="center" color="gray.400" fontSize="sm">No events in this window.</Flex>;
  }
  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

function TopEvents({ rows }) {
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0) || 1;
  return (
    <Box p={4} bg="white" borderRadius="lg" borderWidth="1px">
      <Text fontWeight="700" fontSize="sm" color="#111827" mb={2}>Top events</Text>
      {!rows.length ? <Text fontSize="xs" color="gray.400">No events.</Text> : (
        <Table size="sm" variant="simple">
          <Thead><Tr><Th>Event</Th><Th isNumeric>Count</Th><Th w="35%">Share</Th></Tr></Thead>
          <Tbody>
            {rows.map((r) => (
              <Tr key={r.event}>
                <Td><Text fontFamily="mono" fontSize="xs">{r.event}</Text></Td>
                <Td isNumeric>{fmtInt(r.count)}</Td>
                <Td><Progress value={(r.count / max) * 100} size="sm" borderRadius="full" colorScheme="purple" /></Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Box>
  );
}

function TopEmitters({ rows, loading }) {
  return (
    <Box p={4} bg="white" borderRadius="lg" borderWidth="1px">
      <Text fontWeight="700" fontSize="sm" color="#111827" mb={2}>Top event-emitting accounts</Text>
      {loading ? <Flex h="80px" align="center" justify="center"><Spinner size="sm" /></Flex> :
        !rows.length ? <Text fontSize="xs" color="gray.400">No emitters in window.</Text> : (
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead><Tr><Th>Account</Th><Th isNumeric>Events</Th><Th isNumeric>Last-min tps</Th><Th>Share</Th></Tr></Thead>
              <Tbody>
                {rows.map((r) => (
                  <Tr key={r.customer_id}>
                    <Td>{r.business_name}</Td>
                    <Td isNumeric>{fmtInt(r.events)}</Td>
                    <Td isNumeric>{fmtTps(r.avg_tps_last_min)}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <Progress value={r.share_pct} size="sm" w="80px" borderRadius="full" colorScheme="purple" />
                        <Text fontSize="xs" color="gray.500" minW="42px">{fmtPct(r.share_pct)}</Text>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
    </Box>
  );
}
